import type {
  EntryFlag,
  PrebillCounts,
  PrebillMatter,
  TimeEntry,
  WaitingRow
} from "../types";
import { formatAmount, formatHours } from "../utils";

export const openFlags = (entry: TimeEntry): EntryFlag[] =>
  entry.flags
    .filter((f) => !f.resolution)
    .sort(
      (a, b) => Number(b.severity === "error") - Number(a.severity === "error")
    );

export const isBlocking = (entry: TimeEntry): boolean =>
  entry.status === "released" &&
  entry.flags.some((f) => !f.resolution && f.severity === "error");

export const flagSource = (flag: EntryFlag): string => {
  if (flag.source === "company")
    return `${flag.source_label || "The company"}'s approved rate`;
  if (flag.source === "guideline") return "Client guideline";
  return "Firm rule";
};

export type FixPreview =
  | {
      kind: "rate";
      from: number;
      to: number;
      amountFrom: number;
      amountTo: number;
    }
  | { kind: "hours"; from: number; to: number }
  | { kind: "narrative"; text: string }
  | null;

export const fixPreview = (entry: TimeEntry, flag: EntryFlag): FixPreview => {
  const { fix } = flag;
  if (!fix) return null;
  const value = Number(fix.value);
  if (fix.kind === "rate_override" && Number.isFinite(value))
    return {
      kind: "rate",
      from: entry.effective_rate,
      to: value,
      amountFrom: entry.amount,
      amountTo: Math.round(entry.duration_hours * value * 100) / 100
    };
  if (fix.kind === "hours" && Number.isFinite(value))
    return { kind: "hours", from: entry.duration_hours, to: value };
  if (fix.kind === "narrative" && typeof fix.value === "string" && fix.value)
    return { kind: "narrative", text: fix.value };
  return null;
};

export const fixButton = (flag: EntryFlag): string => {
  if (!flag.fix) return "";
  if (flag.fix.kind === "reviewed") return flag.fix.label || "Mark reviewed";
  if (flag.fix.kind === "rate_override" && Number(flag.fix.value) === 0)
    return "No charge";
  return "Apply fix";
};

const DONE_TAGS = ["Capped", "Reviewed", "Fixed"];

/** The tag a fix left on the entry, if any. */
export const fixedTag = (entry: TimeEntry): string | null =>
  DONE_TAGS.find((t) => entry.tags.includes(t)) ?? null;

export const isFlat = (fee?: string | null) => Boolean(fee?.startsWith("flat"));

export const countsLine = (
  counts: Pick<PrebillCounts, "entries" | "hours" | "value">,
  fee?: string | null,
  flatFee?: number | null
): string => {
  const money = isFlat(fee)
    ? flatFee
      ? `Flat fee ${formatAmount(flatFee)}`
      : "Flat fee"
    : formatAmount(counts.value);
  return `${counts.entries} ${
    counts.entries === 1 ? "entry" : "entries"
  } · ${formatHours(counts.hours)} · ${money}`;
};

export const blockingNote = (matter: PrebillMatter): string | null => {
  const n = matter.entries.filter(isBlocking).length;
  if (!n) return null;
  const what = matter.guidelines.length
    ? "the client's billing guidelines"
    : "a firm rule";
  return `${n} ${
    n === 1 ? "entry breaks" : "entries break"
  } ${what}. Fix, dismiss or return ${
    n === 1 ? "it" : "them"
  } to approve this matter.`;
};

export type EntryFilter = { flaggedOnly: boolean; userId: string | null };

export const filterEntries = (entries: TimeEntry[], filter: EntryFilter) =>
  entries.filter(
    (e) =>
      (!filter.flaggedOnly || openFlags(e).length > 0) &&
      (!filter.userId || e.user_id === filter.userId)
  );

export const waitingText = (row: WaitingRow): string =>
  `${row.count} ${row.count === 1 ? "entry" : "entries"} not released${
    row.days > 1 ? ` · ${row.days} days` : ""
  }`;

export const firstName = (name?: string) =>
  (name || "the timekeeper").split(" ")[0];
